import { useState } from "react";
import { Mail, MessageCircle, Sparkles, Send, CheckCircle2 } from "lucide-react";

const faqs = [
  {
    q: "Is the program really free?",
    a: "Yes — 100% free for every family. Jerseys, equipment, and coaching are all included.",
  },
  {
    q: "My child has never played basketball. Is that okay?",
    a: "Absolutely. Most of our players are new to the game. We teach everything from scratch at each player's own pace.",
  },
  {
    q: "How old do players need to be?",
    a: "UHL is open to a wide range of ages and experience levels. Players are grouped by ability and comfort level to ensure a great experience for everyone — email us with your player's age and we'll let you know if it's a good fit.",
  },
  {
    q: "Where are practices held?",
    a: "Program details — including location and schedule — will be shared after launch. Email unifiedhoopsleague@gmail.com to join our interest list and be the first to hear.",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `UHL Website · Message from ${form.name || "a visitor"}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.location.href = `mailto:unifiedhoopsleague@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <div data-testid="contact-page">
      <section className="basketball-bg-accent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-10 md:pt-24 md:pb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 text-orange-700 text-sm font-extrabold uppercase tracking-wider">
            <Sparkles className="w-4 h-4" />
            Let's Talk
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Have a question? <span className="text-blue-600">We'd love to hear from you.</span>
          </h1>
          <p className="mt-5 text-lg font-medium leading-relaxed text-slate-600 max-w-2xl">
            Whether you're curious about registration, volunteering, or just
            want to say hi — no question is too small. We usually get back to
            families within 1–2 days.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-5 gap-10">
          {/* FORM */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-white rounded-3xl p-8 md:p-10 border border-slate-100 shadow-xl shadow-blue-900/5"
          >
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
              Send us a message
            </h2>
            <p className="mt-2 text-slate-600 font-medium">
              Fill out the form below and your email app will open with a
              ready-to-send message to us.
            </p>

            <div className="mt-8 space-y-5">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-black text-slate-700 mb-2"
                >
                  Your Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Jordan Smith"
                  className="w-full rounded-2xl border border-slate-200 px-5 py-3.5 font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-black text-slate-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="w-full rounded-2xl border border-slate-200 px-5 py-3.5 font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-black text-slate-700 mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us what's on your mind..."
                  className="w-full rounded-2xl border border-slate-200 px-5 py-3.5 font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="inline-flex items-center gap-2 bg-orange-500 text-white hover:bg-orange-600 hover:shadow-lg hover:-translate-y-0.5 transition-all font-bold px-8 py-4 rounded-full text-lg"
              >
                <Send className="w-5 h-5" /> Send Message
              </button>

              <p className="text-xs text-slate-500 font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Opens your email app so you can review before sending.
              </p>
            </div>
          </form>

          {/* CONTACT INFO + FAQ */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-blue-600 rounded-3xl p-8 text-white">
              <MessageCircle className="w-8 h-8 text-orange-300" />
              <h3 className="mt-4 text-xl font-black">Prefer email?</h3>
              <p className="mt-2 text-blue-100 font-medium leading-relaxed">
                Just send us a note directly. We read every message and reply
                personally.
              </p>
              <a
                href="mailto:unifiedhoopsleague@gmail.com"
                data-testid="contact-direct-email"
                className="mt-5 inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-blue-50 transition-all font-bold px-6 py-3 rounded-full"
              >
                <Mail className="w-4 h-4" /> unifiedhoopsleague@gmail.com
              </a>
            </div>

            <div
              data-testid="contact-faq"
              className="bg-white rounded-3xl p-8 border border-slate-100"
            >
              <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-500">
                Quick FAQ
              </p>
              <ul className="mt-5 space-y-5">
                {faqs.map((f, i) => (
                  <li key={i}>
                    <p className="font-black text-slate-900">{f.q}</p>
                    <p className="mt-1 text-slate-600 font-medium leading-relaxed text-sm">
                      {f.a}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
